import React, { useState, useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { MenuProduct } from '../services/MenuService.ts';
import { searchProducts } from '../services/AdminMenuService.ts';
import './ProductPicker.css';

interface ProductPickerProps {
  /** All available products to search through */
  products: MenuProduct[];
  /** Map of product ID -> MenuProduct for fast lookup */
  productMap: Map<string, MenuProduct>;
  /** Currently selected product IDs (ordered) */
  selectedProductIds: string[];
  /** Called when a product is added (multi-select mode) */
  onAdd: (productId: string) => void;
  /** Called when a product is removed (multi-select mode) */
  onRemove: (productId: string) => void;
  /** Called when multiple products are added at once */
  onAddMultiple?: (productIds: string[]) => void;
  /** Called when multiple products are removed at once */
  onRemoveMultiple?: (productIds: string[]) => void;
  /** Called to clear all selected products */
  onClearAll?: () => void;
  /** Called when products are reordered via drag-and-drop */
  onReorder?: (reorderedIds: string[]) => void;
  /** Single-select mode: only one product can be picked */
  singleSelect?: boolean;
  /** Called when a product is picked in single-select mode */
  onSelect?: (productId: string) => void;
  /** Called to clear the single selection */
  onClearSelection?: () => void;
  /** Label shown above the picker */
  label?: string;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
}

type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock' | 'unknown';

function getStockStatus(product: MenuProduct): StockStatus {
  if (!product.variants || product.variants.length === 0) return 'unknown';

  const totalQuantity = product.variants.reduce((sum, v) => {
    if (v.quantity === null || v.quantity === undefined) return sum;
    return sum + v.quantity;
  }, 0);

  const hasAnyQuantityData = product.variants.some(
    v => v.quantity !== null && v.quantity !== undefined
  );

  if (!hasAnyQuantityData) return 'unknown';
  if (totalQuantity === 0) return 'out_of_stock';
  if (totalQuantity <= 5) return 'low_stock';
  return 'in_stock';
}

function getStockLabel(status: StockStatus): string {
  switch (status) {
    case 'in_stock': return 'In Stock';
    case 'low_stock': return 'Low Stock';
    case 'out_of_stock': return 'Out of Stock';
    case 'unknown': return '';
  }
}

const PLACEHOLDER_IMG = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">' +
  '<rect width="48" height="48" fill="#f0f0f0" rx="4"/>' +
  '<text x="24" y="28" text-anchor="middle" font-size="20" fill="#ccc">&#x1F33F;</text>' +
  '</svg>'
);

const ProductPicker: React.FC<ProductPickerProps> = ({
  products,
  productMap,
  selectedProductIds,
  onAdd,
  onRemove,
  onAddMultiple,
  onRemoveMultiple,
  onClearAll,
  onReorder,
  singleSelect = false,
  onSelect,
  onClearSelection,
  label = 'Products',
  searchPlaceholder = 'Search by name, brand, category, strain...',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeBrand, setActiveBrand] = useState<string | null>(null);
  const [hideLowStock, setHideLowStock] = useState(false);
  const [oosConfirmProduct, setOosConfirmProduct] = useState<MenuProduct | null>(null);

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination || !onReorder) return;
    if (result.source.index === result.destination.index) return;
    const reordered = [...selectedProductIds];
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    onReorder(reordered);
  }, [selectedProductIds, onReorder]);

  // Pre-filter by brand and stock (used for category chip counts)
  const productsForCategoryCounts = useMemo(() => {
    let result = products;
    if (activeBrand) {
      result = result.filter(p => p.brand?.name === activeBrand);
    }
    if (hideLowStock) {
      result = result.filter(p => {
        const status = getStockStatus(p);
        return status !== 'low_stock' && status !== 'out_of_stock';
      });
    }
    return result;
  }, [products, activeBrand, hideLowStock]);

  // Derive unique categories sorted by product count (responsive to brand & stock filters)
  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    productsForCategoryCounts.forEach(p => {
      if (p.category) {
        counts.set(p.category, (counts.get(p.category) || 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
  }, [productsForCategoryCounts]);

  // Derive unique brands sorted alphabetically
  const brands = useMemo(() => {
    const counts = new Map<string, number>();
    products.forEach(p => {
      if (p.brand?.name) {
        counts.set(p.brand.name, (counts.get(p.brand.name) || 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [products]);

  // Products filtered by active category, brand, and stock
  const filteredProducts = useMemo(() => {
    let result = products;
    if (activeCategory) {
      result = result.filter(p => p.category === activeCategory);
    }
    if (activeBrand) {
      result = result.filter(p => p.brand?.name === activeBrand);
    }
    if (hideLowStock) {
      result = result.filter(p => {
        const status = getStockStatus(p);
        return status !== 'low_stock' && status !== 'out_of_stock';
      });
    }
    return result;
  }, [products, activeCategory, activeBrand, hideLowStock]);

  // Whether any filters are active (category, brand, or search)
  const hasActiveFilters = !!(searchQuery.trim() || activeCategory || activeBrand);

  const searchResults = useMemo(() => {
    if (!hasActiveFilters) return [];
    if (!searchQuery.trim()) {
      return filteredProducts;
    }
    return searchProducts(searchQuery, filteredProducts);
  }, [searchQuery, filteredProducts, hasActiveFilters]);

  // Determine which results are not yet selected (for Select All)
  const unselectedResults = useMemo(() => {
    return searchResults.filter(p => !selectedProductIds.includes(p.id));
  }, [searchResults, selectedProductIds]);

  const allResultsSelected = searchResults.length > 0 && unselectedResults.length === 0;

  const handleProductClick = useCallback((product: MenuProduct) => {
    if (singleSelect) {
      const stock = getStockStatus(product);
      if (stock === 'out_of_stock') {
        setOosConfirmProduct(product);
        return;
      }
      onSelect?.(product.id);
      return;
    }

    const isSelected = selectedProductIds.includes(product.id);
    if (isSelected) {
      onRemove(product.id);
      return;
    }

    const stock = getStockStatus(product);
    if (stock === 'out_of_stock') {
      setOosConfirmProduct(product);
      return;
    }

    onAdd(product.id);
  }, [selectedProductIds, onAdd, onRemove, singleSelect, onSelect]);

  const handleConfirmOos = useCallback(() => {
    if (oosConfirmProduct) {
      if (singleSelect) {
        onSelect?.(oosConfirmProduct.id);
      } else {
        onAdd(oosConfirmProduct.id);
      }
      setOosConfirmProduct(null);
    }
  }, [oosConfirmProduct, onAdd, singleSelect, onSelect]);

  const handleSelectAll = useCallback(() => {
    if (!onAddMultiple || unselectedResults.length === 0) return;
    const idsToAdd = unselectedResults.map(p => p.id);
    onAddMultiple(idsToAdd);
  }, [onAddMultiple, unselectedResults]);

  const handleClearSelection = useCallback(() => {
    if (onClearAll) {
      onClearAll();
    }
  }, [onClearAll]);

  const staleProductIds = useMemo(() =>
    selectedProductIds.filter(pid => !productMap.get(pid)),
    [selectedProductIds, productMap]
  );

  const handleRemoveStale = useCallback(() => {
    if (onRemoveMultiple) {
      onRemoveMultiple(staleProductIds);
    } else {
      staleProductIds.forEach(pid => onRemove(pid));
    }
  }, [staleProductIds, onRemove, onRemoveMultiple]);

  // Single-select: resolve the selected product for display
  const singleSelectedProduct = singleSelect && selectedProductIds.length > 0
    ? productMap.get(selectedProductIds[0])
    : null;

  return (
    <div className="pp-container">
      {/* Single-select: show current selection */}
      {singleSelect && selectedProductIds.length > 0 && (
        <div className="pp-single-selected">
          {singleSelectedProduct ? (
            <>
              <div className="pp-single-selected-thumb">
                <img
                  src={singleSelectedProduct.images?.[0]?.url || PLACEHOLDER_IMG}
                  alt=""
                  onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER_IMG; }}
                />
              </div>
              <div className="pp-single-selected-info">
                <span className="pp-single-selected-name">{singleSelectedProduct.name}</span>
                <span className="pp-single-selected-meta">
                  {singleSelectedProduct.brand?.name || 'Unknown Brand'}
                  {singleSelectedProduct.category ? ` · ${singleSelectedProduct.category}` : ''}
                </span>
              </div>
            </>
          ) : (
            <div className="pp-single-selected-info">
              <span className="pp-single-selected-name">{selectedProductIds[0]}</span>
              <span className="pp-single-selected-meta">Product not found on menu</span>
            </div>
          )}
          <button
            className="pp-remove-btn"
            onClick={() => onClearSelection?.()}
            title="Clear selection"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* OOS Confirmation Dialog */}
      {oosConfirmProduct && (
        <div className="pp-oos-overlay" onClick={() => setOosConfirmProduct(null)}>
          <div className="pp-oos-dialog" onClick={e => e.stopPropagation()}>
            <div className="pp-oos-dialog-icon">!</div>
            <h4>Out of Stock Product</h4>
            <p>
              <strong>{oosConfirmProduct.name}</strong> is currently out of stock.
              Adding it to this collection means it may display as unavailable to customers.
            </p>
            <div className="pp-oos-dialog-actions">
              <button
                className="pp-oos-cancel"
                onClick={() => setOosConfirmProduct(null)}
              >
                Cancel
              </button>
              <button
                className="pp-oos-confirm"
                onClick={handleConfirmOos}
              >
                Add Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="pp-search-wrapper">
        <svg className="pp-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M7 12A5 5 0 107 2a5 5 0 000 10zM14 14l-3.5-3.5" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <input
          className="pp-search-input"
          type="text"
          placeholder={searchPlaceholder}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            className="pp-search-clear"
            onClick={() => setSearchQuery('')}
            title="Clear search"
          >
            &times;
          </button>
        )}
      </div>

      {/* Brand Filter Dropdown */}
      <div className="pp-filters-row">
        <div className="pp-brand-filter">
          <select
            className="pp-brand-select"
            value={activeBrand || ''}
            onChange={e => setActiveBrand(e.target.value || null)}
          >
            <option value="">All Brands</option>
            {brands.map(b => (
              <option key={b.name} value={b.name}>
                {b.name} ({b.count})
              </option>
            ))}
          </select>
        </div>

        <label className="pp-hide-low-stock">
          <input
            type="checkbox"
            checked={hideLowStock}
            onChange={e => setHideLowStock(e.target.checked)}
          />
          Hide low/out of stock
        </label>

        {/* Active filter summary */}
        {(activeCategory || activeBrand) && (
          <div className="pp-active-filters">
            {activeCategory && (
              <span className="pp-active-filter-tag">
                {activeCategory}
                <button onClick={() => setActiveCategory(null)}>&times;</button>
              </span>
            )}
            {activeBrand && (
              <span className="pp-active-filter-tag">
                {activeBrand}
                <button onClick={() => setActiveBrand(null)}>&times;</button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Category Filter Chips */}
      {categories.length > 0 && (
        <div className="pp-filter-chips">
          {categories.map(cat => (
            <button
              key={cat.name}
              className={`pp-chip${activeCategory === cat.name ? ' active' : ''}`}
              onClick={() => {
                setActiveCategory(activeCategory === cat.name ? null : cat.name);
              }}
            >
              {cat.name}
              <span className="pp-chip-count">{cat.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Search Results */}
      {hasActiveFilters && (
        <div className="pp-results">
          {searchResults.length === 0 && (
            <div className="pp-no-results">
              No products found{searchQuery ? ` for "${searchQuery}"` : ''}
            </div>
          )}
          {searchResults.map(product => {
            const isSelected = selectedProductIds.includes(product.id);
            const stockStatus = getStockStatus(product);
            const stockLabel = getStockLabel(stockStatus);
            const imgUrl = product.images?.[0]?.url || PLACEHOLDER_IMG;

            return (
              <div
                key={product.id}
                className={`pp-result-item${isSelected ? ' selected' : ''}${stockStatus === 'out_of_stock' ? ' oos' : ''}`}
                onClick={() => handleProductClick(product)}
              >
                {/* Checkbox */}
                <div className={`pp-checkbox${isSelected ? ' checked' : ''}`}>
                  {isSelected && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>

                {/* Thumbnail */}
                <div className="pp-thumb">
                  <img
                    src={imgUrl}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = PLACEHOLDER_IMG;
                    }}
                  />
                </div>

                {/* Product Info */}
                <div className="pp-result-info">
                  <span className="pp-result-name">{product.name}</span>
                  <div className="pp-result-meta">
                    {product.brand?.name && (
                      <span className="pp-meta-brand">{product.brand.name}</span>
                    )}
                    {product.category && (
                      <span className="pp-meta-tag">{product.category}</span>
                    )}
                    {product.strainType && (
                      <span className="pp-meta-tag strain">{product.strainType}</span>
                    )}
                  </div>
                </div>

                {/* Stock Status */}
                {stockLabel && (
                  <span className={`pp-stock pp-stock-${stockStatus}`}>
                    {stockLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Selected Products (multi-select only) */}
      {!singleSelect && selectedProductIds.length > 0 && (
        <>
          <p className="pp-selected-header">
            {label} ({selectedProductIds.length})
            {onReorder && selectedProductIds.length > 1 && (
              <span className="pp-reorder-hint">Drag to reorder</span>
            )}
          </p>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="selected-products">
              {(provided) => (
                <div
                  className="pp-selected-list"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {selectedProductIds.map((pid, pidIndex) => {
                    const product = productMap.get(pid);
                    const isStale = !product;
                    const imgUrl = product?.images?.[0]?.url || PLACEHOLDER_IMG;
                    const stockStatus = product ? getStockStatus(product) : 'unknown';

                    return (
                      <Draggable key={pid} draggableId={pid} index={pidIndex} isDragDisabled={!onReorder}>
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`pp-selected-item${isStale ? ' stale' : ''}${stockStatus === 'out_of_stock' ? ' oos' : ''}${snapshot.isDragging ? ' dragging' : ''}`}
                          >
                            {onReorder && (
                              <span className="pp-drag-handle" {...dragProvided.dragHandleProps}>
                                ⠿
                              </span>
                            )}
                            <span className="pp-selected-order">{pidIndex + 1}</span>

                            {!isStale && (
                              <div className="pp-selected-thumb">
                                <img
                                  src={imgUrl}
                                  alt=""
                                  loading="lazy"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = PLACEHOLDER_IMG;
                                  }}
                                />
                              </div>
                            )}

                            <div className="pp-selected-info">
                              <span className="pp-selected-name">
                                {product ? product.name : pid}
                              </span>
                              {product && (
                                <span className="pp-selected-brand">
                                  {product.brand?.name || 'Unknown Brand'}
                                  {product.category ? ` · ${product.category}` : ''}
                                </span>
                              )}
                              {isStale && <span className="pp-stale-badge">Not on menu</span>}
                              {stockStatus === 'out_of_stock' && !isStale && (
                                <span className="pp-oos-badge">Out of Stock</span>
                              )}
                            </div>

                            <button
                              className="pp-remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemove(pid);
                              }}
                              title="Remove product"
                            >
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}

      {/* Bottom Action Buttons (multi-select only) */}
      {!singleSelect && <div className="pp-bottom-actions">
        {onAddMultiple && hasActiveFilters && searchResults.length > 1 && (
          <button
            className="pp-select-all-btn"
            onClick={handleSelectAll}
            disabled={allResultsSelected}
            title={allResultsSelected ? 'All visible results are already selected' : `Add all ${unselectedResults.length} visible results`}
          >
            Select All ({unselectedResults.length})
          </button>
        )}
        {staleProductIds.length > 0 && (
          <button
            className="pp-clear-btn"
            onClick={handleRemoveStale}
          >
            Remove Products Not on Menu ({staleProductIds.length})
          </button>
        )}
        {onClearAll && selectedProductIds.length > 0 && (
          <button
            className="pp-clear-btn"
            onClick={handleClearSelection}
          >
            Remove All Products
          </button>
        )}
      </div>}
    </div>
  );
};

export default ProductPicker;
